import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '../../Components/Date/Datepicker';
import { useSnackbar } from '../../Context/SnackbarProvider';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COLORS } from '../../Styles/Colors';
import apiClient from '../../Services/ApiClient';

// Merge types mirror the old AIMS report dialog: Abatix / Non-Abatix / Equipment.
// "Equipment" is now called "Assets" (assets_transaction).
type MergeType = 'abatix' | 'nonAbatix' | 'assets';
type SortBy = 'location' | 'customer' | 'employee' | 'poJob';

interface Column {
  key: string;
  label: string;
}

// Consumables reports (Abatix + Non-Abatix) — same columns/headers as the main
// Transactions Merge & Export / table export. Type-specific fields (P21*, IMS)
// render as '-' when absent.
const CONSUMABLE_COLUMNS: Column[] = [
  { key: 'transaction_date',    label: 'Date' },
  { key: 'CustomerName',        label: 'Account' },
  { key: 'location_name',       label: 'Location' },
  { key: 'po_job_number',       label: 'PO/Job No.' },
  { key: 'product_code',        label: 'Part No.' },
  { key: 'product_description', label: 'Description' },
  { key: 'quantity',            label: 'QTY' },
  { key: 'uom',                 label: 'UOM' },
  { key: 'unit_price',          label: 'Price' },
  { key: 'ext_price',           label: 'Ext. Price' },
  { key: 'employee_name',       label: 'Employee' },
  { key: 'requester_name',      label: 'Requester' },
  { key: 'bin',                 label: 'Bin' },
  { key: 'category_name',       label: 'Category' },
  { key: 'P21OrderNumber',      label: 'P21 Order No.' },
  { key: 'P21InvoiceNumber',    label: 'P21 Invoice No.' },
  { key: 'ims_order_no',        label: 'IMS Order No.' },
];

// Assets report — match Asset Transactions page columns & header names.
const ASSET_COLUMNS: Column[] = [
  { key: 'company_name', label: 'Account' },
  { key: 'location_name', label: 'Location' },
  { key: 'part_number', label: 'Part No.' },
  { key: 'name', label: 'Description' },
  { key: 'serial_number', label: 'Serial No.' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'manufacturer_id', label: 'Manufacturer ID' },
  { key: 'po_number', label: 'PO/Job No.' },
  { key: 'checkout_datetime', label: 'Checkout Date' },
  { key: 'checkout_user_name', label: 'Checkout Employee' },
  { key: 'checkout_requester_name', label: 'Checkout Requester' },
  { key: 'issued_bin_location_name', label: 'Checkout Bin' },
  { key: 'checkin_datetime', label: 'Check-in Date' },
  { key: 'checkin_user_name', label: 'Check-in Employee' },
  { key: 'checkin_requester_name', label: 'Check-in Requester' },
  { key: 'returned_bin_location_name', label: 'Check-in Bin' },
  { key: 'note', label: 'Note' },
  { key: 'is_active', label: 'Active' },
  { key: 'transaction_number', label: 'Transaction No.' },
  { key: 'created_at', label: 'Created At' },
  { key: 'updated_at', label: 'Updated At' },
];

interface ReportSection {
  title: string;
  columns: Column[];
  rows: any[];
}

interface InitialFilters {
  startDate: Date | null;
  endDate: Date | null;
  selectedOvs: string;
  columnSearches: Record<string, string>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialFilters: InitialFilters;
}

const MERGE_TYPE_META: { type: MergeType; label: string; title: string }[] = [
  { type: 'abatix',    label: 'Abatix',     title: 'Abatix Consumables Report' },
  { type: 'nonAbatix', label: 'Non-Abatix', title: 'Non-Abatix Consumables Report' },
  { type: 'assets',    label: 'Assets',     title: 'Assets Report' },
];

function buildFilename(ext: string, startDate: Date | null, endDate: Date | null): string {
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  if (startDate && endDate) {
    return `Asset_Transactions_Merged_${fmt(startDate)}_to_${fmt(endDate)}.${ext}`;
  }
  return `Asset_Transactions_Merged_All_Dates.${ext}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function cellValue(row: any, key: string): string {
  if (key === 'ext_price') {
    const explicit = row.ext_price ?? row.amount;
    if (explicit != null && explicit !== '') return String(explicit);
    const qty = Number(row.quantity ?? 0);
    const price = Number(row.unit_price ?? 0);
    return (qty * price).toFixed(2);
  }
  if (key === 'bin') {
    const val = row.bin ?? row.bin_name ?? row.bin_location_id;
    if (val == null || val === '') return '-';
    return String(val);
  }
  if (key === 'is_active') {
    const val = row.is_active;
    if (val == null || val === '') return '-';
    return val ? 'Y' : 'N';
  }
  const val = row[key];
  if (val == null || val === '') return '-';
  return String(val);
}

function generateCsv(sections: ReportSection[]): string {
  const blocks = sections.map((section) => {
    const title = `"${section.title}"`;
    const headers = section.columns.map((c) => `"${c.label}"`).join(',');
    const lines = section.rows.map((row) =>
      section.columns
        .map((c) => `"${cellValue(row, c.key).replace(/"/g, '""')}"`)
        .join(','),
    );
    const body = lines.length > 0 ? lines : ['"No records"'];
    return [title, headers, ...body].join('\n');
  });
  // Blank line between sections, mirroring the old per-sheet layout.
  return blocks.join('\n\n');
}

function generatePdf(sections: ReportSection[], startDate: Date | null, endDate: Date | null): jsPDF {
  const doc = new jsPDF('l', 'mm', 'a4');
  const fmt = (d: Date) => d.toLocaleDateString();
  const dateRange = startDate && endDate ? `${fmt(startDate)} – ${fmt(endDate)}` : 'All Dates';

  doc.setFontSize(16);
  doc.text('Asset Transactions — Merged Export', 14, 15);
  doc.setFontSize(10);
  doc.text(`Date Range: ${dateRange}`, 14, 22);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);

  let startY = 36;

  sections.forEach((section) => {
    doc.setFontSize(12);
    doc.text(section.title, 14, startY);

    const headers = section.columns.map((c) => c.label);
    const body = section.rows.map((row) => section.columns.map((c) => cellValue(row, c.key)));

    autoTable(doc, {
      head: [headers],
      body: body.length > 0 ? body : [[{ content: 'No records', colSpan: headers.length }]] as any,
      startY: startY + 3,
      theme: 'grid',
      styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' },
      headStyles: { fillColor: [32, 178, 170], textColor: 255, fontStyle: 'bold' },
      margin: { left: 10, right: 10 },
    });

    startY = (doc as any).lastAutoTable.finalY + 10;
  });

  return doc;
}

export const AssetTransactionMergeExportDialog: React.FC<Props> = ({
  open,
  onClose,
  initialFilters,
}) => {
  const { showSnackbar } = useSnackbar();

  const [mergeTypes, setMergeTypes] = useState<Set<MergeType>>(new Set<MergeType>(['assets']));
  const [sortBy, setSortBy] = useState<Set<SortBy>>(new Set<SortBy>());
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStartDate(initialFilters.startDate);
    setEndDate(initialFilters.endDate);
    setMergeTypes(new Set<MergeType>(['assets']));
    setSortBy(new Set<SortBy>());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only on open
  }, [open]);

  const toggleMergeType = (type: MergeType) => {
    setMergeTypes((prev) => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const toggleSortBy = (col: SortBy) => {
    setSortBy((prev) => {
      const next = new Set(prev);
      next.has(col) ? next.delete(col) : next.add(col);
      return next;
    });
  };

  const noTypeSelected = mergeTypes.size === 0;

  const dateParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate.toISOString().split('T')[0]);
    if (endDate) params.set('endDate', endDate.toISOString().split('T')[0]);
    if (initialFilters.selectedOvs) params.set('ovid', initialFilters.selectedOvs);
    return params;
  };

  // Asset (equipment) export — carries the asset grid's column searches too.
  const fetchAssetRows = async (): Promise<any[]> => {
    const params = dateParams();
    const assetSort = Array.from(sortBy); // asset endpoint supports all four
    if (assetSort.length > 0) params.set('sortBy', assetSort.join(','));
    Object.entries(initialFilters.columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    const res = await apiClient(`/api/assets/transaction/export?${params.toString()}`);
    if (!res.ok) throw new Error('asset export failed');
    return res.json();
  };

  // Consumables export (Abatix / Non-Abatix) — the transactions endpoint only
  // understands location/poJob/employee sorting, so 'customer' is dropped here.
  const fetchConsumableRows = async (mergeType: 'abatix' | 'nonAbatix'): Promise<any[]> => {
    const params = dateParams();
    params.set('mergeTypes', mergeType);
    const txnSort = Array.from(sortBy).filter((s) => s !== 'customer');
    if (txnSort.length > 0) params.set('sortBy', txnSort.join(','));
    const res = await apiClient(`/api/transactions/export?${params.toString()}`);
    if (!res.ok) throw new Error(`${mergeType} export failed`);
    return res.json();
  };

  const fetchSections = async (): Promise<ReportSection[] | null> => {
    try {
      const sections: ReportSection[] = [];
      for (const { type, title } of MERGE_TYPE_META) {
        if (!mergeTypes.has(type)) continue;
        if (type === 'assets') {
          sections.push({ title, columns: ASSET_COLUMNS, rows: await fetchAssetRows() });
        } else {
          sections.push({ title, columns: CONSUMABLE_COLUMNS, rows: await fetchConsumableRows(type) });
        }
      }
      return sections;
    } catch {
      showSnackbar('Export failed. Please try again.', 'error');
      return null;
    }
  };

  const handleExportCsv = async () => {
    setLoadingCsv(true);
    try {
      const sections = await fetchSections();
      if (!sections) return;
      triggerDownload(
        new Blob([generateCsv(sections)], { type: 'text/csv;charset=utf-8;' }),
        buildFilename('csv', startDate, endDate),
      );
    } finally {
      setLoadingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    setLoadingPdf(true);
    try {
      const sections = await fetchSections();
      if (!sections) return;
      const doc = generatePdf(sections, startDate, endDate);
      doc.save(buildFilename('pdf', startDate, endDate));
    } finally {
      setLoadingPdf(false);
    }
  };

  const isLoading = loadingPdf || loadingCsv;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Merge &amp; Export</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Reporting to Merge:
        </Typography>
        <FormGroup row sx={{ gap: 1, mb: 2 }}>
          {MERGE_TYPE_META.map(({ type, label }) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={mergeTypes.has(type)}
                  onChange={() => toggleMergeType(type)}
                  sx={{ color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Merge Reporting By:
        </Typography>
        <FormGroup row sx={{ gap: 1, mb: 2 }}>
          {([
            { col: 'location' as SortBy, label: 'Location' },
            { col: 'customer' as SortBy, label: 'Customer' },
            { col: 'employee' as SortBy, label: 'Employee' },
            { col: 'poJob' as SortBy, label: 'PO/Job No.' },
          ]).map(({ col, label }) => (
            <FormControlLabel
              key={col}
              control={
                <Checkbox
                  checked={sortBy.has(col)}
                  onChange={() => toggleSortBy(col)}
                  sx={{ color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Select Date Range:
        </Typography>
        <Box sx={{ mb: 1 }}>
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'capitalize' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExportPdf}
          disabled={isLoading || noTypeSelected}
          sx={{
            textTransform: 'capitalize',
            backgroundColor: COLORS.teal,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {loadingPdf ? 'Exporting…' : 'Export PDF'}
        </Button>
        <Button
          variant="contained"
          onClick={handleExportCsv}
          disabled={isLoading || noTypeSelected}
          sx={{
            textTransform: 'capitalize',
            backgroundColor: COLORS.teal,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {loadingCsv ? 'Exporting…' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
